import { Card, Row, Col, Modal, Input, Spin } from "antd";
import TriggerComponent from "./TriggerComponent";
import CertificateComponent from "./CertificateComponent";
import StatusAnimationComponent from "./StatusAnimationComponent";
import EditableComponent from "./EditableComponent";
import {
  DEVICE_STATUS,
  HARDWARE_TYPE,
  OPP_CONSTANTS,
  ORDER_DATA_SUMMARY,
  PRODUCT_THEME,
} from "../../constants/OppContants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createRef, useEffect, useRef, useState } from "react";
import QRCodeComponent from "../QRCodeComponent";
import {
  fetchCustomDeviceToken,
  fetchLatestDeviceActions,
  setDeviceActionToTPS,
  triggerAction,
  updateDeviceNumberById,
  updateOrderDevices
} from "../../services/fetchApis";
import {
  ActionCommands,
  PROJECTS,
  QC_VALIDATIONS,
} from "../../constants/Actions";
import Dropzone from "react-dropzone";
import yaml from "js-yaml";
import moment from "moment";
import { ToastComponent } from "../ToastComponent";

const DeviceColumnComponent = ({
  orderId,
  deviceMapData,
  onDeviceQCStatusUpdated,
  onOverallStatusUpdated,
  productType,
  groupId,
  schoolId,
  schoolName,
  packageName,
  loginId,
  password,
  disableAllActions,
  deviceActionStatus,
  allTriggered,
  oneNucleusValid
}) => {
  const [devices, setDevices] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stockLocationModal, setStockLocationModal] = useState({ open: false, cardKey: null });
  const [selectedStockLocations, setSelectedStockLocations] = useState({});
  // Loader state for stock location API (per card)
  const [stockLocationLoadingMap, setStockLocationLoadingMap] = useState({});
  const setStockLocationLoading = (uniqueKey, value) => {
    setStockLocationLoadingMap(prev => ({ ...prev, [uniqueKey]: value }));
  }
  const [serialIdErrors, setSerialIdErrors] = useState({});
  const [installationReports, setInstallationReports] = useState({});

  useEffect(() => {
    if (!deviceMapData) return;
    const initialStockLocations = {};
    Object.keys(deviceMapData).forEach((hardwareModel) => {
      const techbookGroups = deviceMapData[hardwareModel];
      Object.keys(techbookGroups).forEach((techbookKey) => {
        const devicesArray = techbookGroups[techbookKey] || [];
        if (devicesArray.length > 0) {
          const firstDevice = devicesArray[0];
          const isTechbook = firstDevice?.isTechbook;
          const uniqueKey = `${productType}-${hardwareModel}-${firstDevice.deviceType}-${isTechbook}`;
          // Find the first non-empty stockLocation in this group
          const foundStockLoc = devicesArray.find(d => d.stockLocation) || {};
          if (foundStockLoc.stockLocation) {
            initialStockLocations[uniqueKey] = foundStockLoc.stockLocation;
          }
        }
      });
    });
    setSelectedStockLocations(prev => ({ ...initialStockLocations, ...prev }));
  }, [deviceMapData, productType]);
  const STOCK_LOCATIONS = [
    "Bangalore",
    "Trichy",
    "Hyderabad",
    "Kolkata",
    "Bhiwandi",
    "Indore",
    "Ghaziabad",
    "Zirakpur"
  ];
  const [deviceTriggerStatus, setDeviceTriggerStatus] = useState(null);
  const [hardwaresQCStatus, setHardwaresQCStatus] = useState([]);
  const [hardwaresForStatusRefresh, setHardwaresForStatusRefresh] = useState(
    []
  );
  const countdownIntervalRef = useRef(null);
  const hardwaresForStatusRefreshRef = useRef(hardwaresForStatusRefresh);
  const [triggeredAll, setTriggeredAllQC] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [enabledSDCardCount, setEnabledSDCardCount] = useState(0);
  const [triggerModal, setTriggerModal] = useState({ visible: false, mdmDeviceId: null, deviceId: null, reason: "", existingOrders: [] });
  const isTriggerAllInProgressRef = useRef(false);
  const [confirmedDevices, setConfirmedDevices] = useState({});

  const inputRefs = useRef([]);

  useEffect(() => {
    setTriggeredAllQC(allTriggered);
  }, [allTriggered]);

  useEffect(() => {
    hardwaresForStatusRefreshRef.current = hardwaresForStatusRefresh;
  }, [hardwaresForStatusRefresh]);

  useEffect(() => {
    countdownIntervalRef.current = setInterval(() => {
      if (document.hasFocus()) {
        if (hardwaresForStatusRefreshRef.current.length > 0) {
          fetchHardwaresRefreshedData();
        }
      }
    }, 2000);

    return () => clearInterval(countdownIntervalRef.current);
  }, []);

  useEffect(() => {
    if (disableAllActions) {
      clearInterval(countdownIntervalRef.current);
    }
  }, [disableAllActions]);

  useEffect(() => {
    let devicesList = {};

    if (deviceMapData) {
      Object.keys(deviceMapData).forEach((deviceModel) => {
        const techbookGroups = deviceMapData[deviceModel];
        Object.keys(techbookGroups).forEach((techbookKey) => {
          const devicesArray = techbookGroups[techbookKey] || [];
          devicesArray.forEach((deviceData) => {
            const isValidNo =
              deviceData?.deviceNo !== null &&
              deviceData?.deviceNo?.trim().length !== 0;

            deviceData = { ...deviceData, isSaved: isValidNo };
            devicesList[deviceData.id] = deviceData;
          });
        });
      });
    }

    setDevices(devicesList);

    // Check installation reports for IFP/TV devices that already have saved serial numbers
    Object.values(devicesList).forEach((device) => {
      const deviceType = device?.deviceType?.toUpperCase();
      const isIFPorTV = deviceType === HARDWARE_TYPE.TV.name.toUpperCase() ||
                        deviceType === HARDWARE_TYPE.IFP?.name?.toUpperCase();
      const serialId = device?.deviceNo;
      if (isIFPorTV && serialId && serialId.length >= 10) {
        checkInstallationReport(device.id, serialId);
      }
    });
  }, [deviceMapData]);

  useEffect(() => {
    if (!!devices) {
      const enabledTabDevices = Object.keys(devices)?.filter((key) => {
        const isValidNo =
          devices[key]?.deviceType?.toUpperCase() ===
          HARDWARE_TYPE.TAB.name.toUpperCase() &&
          devices[key]?.deviceNo !== null &&
          devices[key]?.deviceNo?.length !== 0;

        const isTriggered =
          deviceTriggerStatus?.[devices[key]?.id]?.triggerStatus ?? false;

        return isValidNo && !isTriggered;
      });

      const enabledSDCards = Object.keys(devices)?.filter((key) => {
        const isValidNo =
          devices[key]?.deviceType?.toUpperCase() ===
          HARDWARE_TYPE.SDCARD.name.toUpperCase() &&
          devices[key]?.deviceNo !== null &&
          devices[key]?.deviceNo?.length !== 0;

        return isValidNo;
      });

      setEnabledSDCardCount(enabledSDCards.length);

      const isEnabledDeviceCount = enabledTabDevices.length;
      onDeviceQCStatusUpdated(isEnabledDeviceCount);

      inputRefs.current = Object.keys(devices)?.map((key, index) => {
        inputRefs.current[index] ?? createRef();
      });
    }
  }, [devices, deviceTriggerStatus]);

  useEffect(() => {
    if (!devices || !hardwaresQCStatus) return;
    const enabledTabDevices = Object.keys(devices)?.filter((key) => {
      const isValidNo =
        devices[key]?.deviceType?.toUpperCase() ===
        HARDWARE_TYPE.TAB.name.toUpperCase() &&
        devices[key]?.deviceNo !== null &&
        devices[key]?.deviceNo?.length !== 0;

      const isTriggered =
        deviceTriggerStatus?.[devices[key]?.id]?.triggerStatus ?? false;

      return isValidNo && !isTriggered;
    });

    const isEnabledDeviceCount = enabledTabDevices.length;

    ///  1 Count of Tabs with valid Number
    const validTabDevicesNumbers = Object.keys(devices)?.filter((key) => {
      const isValidNo =
        devices[key]?.deviceType?.toUpperCase() ===
        HARDWARE_TYPE.TAB.name.toUpperCase() &&
        devices[key]?.deviceNo !== null &&
        devices[key]?.deviceNo?.length !== 0;
      return isValidNo;
    });

    ///  2 Count of other hardware with valid Number
    const validOtherDevicesNumbers = Object.keys(devices)?.filter((key) => {
      const isValidNo =
        devices[key]?.deviceType?.toUpperCase() !==
        HARDWARE_TYPE.TAB.name.toUpperCase() &&
        devices[key]?.deviceNo !== null &&
        devices[key]?.deviceNo?.length !== 0;
      return isValidNo;
    });

    /// 3 trigger enableded count has to be 0
    /// 4 QC completed devices with success status
    let successQCDevices = 0;
    if (isEnabledDeviceCount === 0) {
      successQCDevices = Object.keys(devices)?.filter((key) => {
        let status =
          hardwaresQCStatus[devices[key]?.id]?.filter(
            (action) => action.actionId === "DISPLAY_MESSAGE"
          )?.[0] ?? null;
        return (
          status?.status?.toUpperCase() === DEVICE_STATUS.SUCCESS.toUpperCase()
        );
      });
    }
    const validDeviceCount =
      validOtherDevicesNumbers?.length + validTabDevicesNumbers.length;
    if (
      validTabDevicesNumbers?.length === successQCDevices?.length &&
      validDeviceCount === Object.keys(devices)?.length
    ) {
      onOverallStatusUpdated(true);
    } else {
      onOverallStatusUpdated(false);
    }
  }, [devices, hardwaresQCStatus]);

  useEffect(() => {
    if (triggeredAll) {
      (async () => {
        isTriggerAllInProgressRef.current = true;
        let triggerPromises = [];
        deviceMapData &&
          Object.keys(deviceMapData)?.forEach((deviceKey) => {
            Object.keys(deviceMapData[deviceKey])?.forEach((techbookKey) => {
              deviceMapData[deviceKey][techbookKey]?.forEach((keyComponent) => {
                let currentDevice = devices[keyComponent?.id];
                if (
                  currentDevice?.deviceType?.toUpperCase() ===
                  HARDWARE_TYPE.TAB.name.toUpperCase()
                ) {
                  triggerPromises.push(handleQCSubmit(keyComponent?.id));
                }
              });
            });
          });
        await Promise.all(triggerPromises);
        isTriggerAllInProgressRef.current = false;
        // After all triggers, update skipped devices from ref
      })();
    }
  }, [triggeredAll]);

  const handleQRClick = (uniqueKey) => {
    let newVal = isModalOpen != uniqueKey ? uniqueKey : null;
    setIsModalOpen(newVal);
  };

  const handleSerialIdChange = (deviceId, serialId, errorMsg) => {
    const currentDevice = devices[deviceId];
    setSerialIdErrors((prev) => ({
      ...prev,
      [deviceId]: errorMsg,
    }));
    if (currentDevice?.deviceNo != serialId) {
      setDevices((prevDevices) => {
        const updatedDevices = { ...prevDevices };
        updatedDevices[deviceId] = {
          ...updatedDevices[deviceId],
          deviceNo: serialId,
        };
        return updatedDevices;
      });

      // Check for installation report when serial ID is valid
      const deviceType = currentDevice?.deviceType?.toUpperCase();
      const isIFPorTV = deviceType === HARDWARE_TYPE.TV.name.toUpperCase() || 
                        deviceType === HARDWARE_TYPE.IFP?.name?.toUpperCase();
      
      if (isIFPorTV && serialId && serialId.length >= 10) {
        checkInstallationReport(deviceId, serialId);
      } else {
        // Clear report status if serial is invalid
        setInstallationReports((prev) => ({
          ...prev,
          [deviceId]: null,
        }));
      }

      switch (currentDevice?.deviceType.toUpperCase()) {
        case HARDWARE_TYPE.TAB.name.toUpperCase():
          if (serialId.length === HARDWARE_TYPE.TAB.serialIdLength) {
            handleFocusNext(currentDevice?.id);
          }
          break;
        case HARDWARE_TYPE.TV.name.toUpperCase():
          break;
        case HARDWARE_TYPE.TAB.name.toUpperCase():
          break;
        default:
          break;
      }
    }
  };

  const handleFocusNext = (deviceId) => {
    let found = false;

    Object.keys(devices).some((key, index) => {
      if (found) {
        inputRefs.current[index]?.focus();
        return true; // Break out of the loop
      }
      if (key === "" + deviceId) {
        found = true;
      }
      return false; // Continue looping
    });
  };

  const checkInstallationReport = async (deviceId, serialNumber) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GREENFIELD_URL}/api/installation-report/check/${encodeURIComponent(serialNumber)}`
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.hasReport) {
          setInstallationReports((prev) => ({
            ...prev,
            [deviceId]: result.data,
          }));
        } else {
          setInstallationReports((prev) => ({
            ...prev,
            [deviceId]: null,
          }));
        }
      } else {
        setInstallationReports((prev) => ({
          ...prev,
          [deviceId]: null,
        }));
      }
    } catch (error) {
      console.error('Error checking installation report:', error);
      setInstallationReports((prev) => ({
        ...prev,
        [deviceId]: null,
      }));
    }
  };

  const cancelQC = async (deviceId) => {
    /// reset data
    handledActionFailed(deviceId);
  };

  const handleQCSubmit = async (deviceId, reason = null) => {
    const currentDevice = devices[deviceId];

    /// reset data
    setHardwaresQCStatus((prevStatus) => {
      const updatedStatus = { ...prevStatus };
      delete updatedStatus[deviceId];
      return updatedStatus;
    });

    /// set state as its triggered
    setDeviceTriggerStatus((prevDevices) => {
      const updatedDevices = { ...prevDevices };
      updatedDevices[deviceId] = {
        ...currentDevice[deviceId],
        triggerStatus: true,
        errorMessage: null,
      };
      return updatedDevices;
    });

    const registerDevice = await updateDeviceToTps(
      deviceId,
      currentDevice?.deviceNo,
      reason
    );
    if (!registerDevice) return;

    /// get customtoken1 from TPS BE
    const deviceToken = await getTPSDeviceToken(
      deviceId,
      currentDevice.deviceNo
    );

    if (!deviceToken) return;

    /// trigger Action for fcm notification
    const response = await runQCTrigger(deviceId, deviceToken);
    if (response && response?.status === "OK") {
      ///Add to que list for refreshing the data from server
      if (!hardwaresForStatusRefreshRef.current.includes(deviceId)) {
        setHardwaresForStatusRefresh([
          ...hardwaresForStatusRefreshRef.current,
          deviceId,
        ]);
      }
    } else {
      setDeviceTriggerStatus((prevDevices) => {
        const updatedDevices = { ...prevDevices };
        updatedDevices[deviceId] = {
          ...currentDevice[deviceId],
          triggerStatus: false,
          errorMessage: OPP_CONSTANTS.messages.unableToTrigger,
        };
        return updatedDevices;
      });
    }
  };

  /// update and get value or error message
  const updateDeviceToTps = async (deviceId, mdmDeviceId, reason = null) => {
    const currentDevice = devices[deviceId];
    let response = await updateDeviceNumberById(deviceId, mdmDeviceId, reason);
    if (response && response?.status === "OK") {
      if (Array.isArray(response.data) && response.data.length > 0) {
        // Show modal to confirm trigger with reason
        setConfirmedDevices(prev => ({ ...prev, [deviceId]: false }));
        if (isTriggerAllInProgressRef.current) {
          setDeviceTriggerStatus((prevDevices) => {
            const updatedDevices = { ...prevDevices };
            updatedDevices[deviceId] = {
              ...currentDevice[deviceId],
              triggerStatus: false,
              errorMessage: "Device already mapped. Trigger individually",
            };
            return updatedDevices;
          });
        } else {
          setTriggerModal({
            visible: true,
            deviceId,
            mdmDeviceId,
            reason: "",
            existingOrders: response.data,
          });
        }
        return false; // Prevent further processing until user confirms
      }

      setConfirmedDevices(prev => ({ ...prev, [deviceId]: true }));
      return true;
    }

    setDeviceTriggerStatus((prevDevices) => {
      const updatedDevices = { ...prevDevices };
      updatedDevices[deviceId] = {
        ...currentDevice[deviceId],
        triggerStatus: false,
        errorMessage:
          response?.message ?? OPP_CONSTANTS.messages.unableToUpdate,
      };
      return updatedDevices;
    });

    return false;
  };

  const handleConfirmTrigger = async () => {
    const reason = triggerModal.reason.trim();
    if (!reason) return;
    const reasonData = { message: reason, existingOrders: triggerModal.existingOrders.map(o => o.orpOrderId) };
    await handleQCSubmit(triggerModal?.deviceId, reasonData);
    setTriggerModal({ visible: false, mdmDeviceId: null, deviceId: null, reason: "", existingOrders: [] });
  }

  const getTPSDeviceToken = async (deviceId, mdmDeviceId) => {
    const currentDevice = devices[deviceId];

    let response = await fetchCustomDeviceToken(mdmDeviceId);
    if (response?.data?.custom1) {
      return response?.data?.custom1 ?? null;
    }
    setDeviceTriggerStatus((prevDevices) => {
      const updatedDevices = { ...prevDevices };
      updatedDevices[deviceId] = {
        ...currentDevice[deviceId],
        triggerStatus: false,
        errorMessage: response?.data
          ? OPP_CONSTANTS.messages.invalidFcmToken
          : response?.message ?? OPP_CONSTANTS.messages.unableToUpdate,
      };
      return updatedDevices;
    });

    /// Handle error message
    return null;
  };

  const getQCValidationAction = (deviceId) => {
    const currentDevice = devices[deviceId];
    const taPackageName =
      packageName ||
      PRODUCT_THEME[productType]?.packageName +
      process.env.REACT_APP_TA_PACKAGE_EXT;

    let finalStatusMap = QC_VALIDATIONS.map(
      ({ displayTitle, priority, status, id, ...actionData }) => {
        actionData.command = id;
        switch (id) {
          case "DEVICE_MODEL_VALIDATION":
            actionData.params = {
              device_model: currentDevice?.deviceModel,
            };
            break;
          case "CONFIGURATION_VALIDATION":
            actionData.params = {
              configuration_id: currentDevice?.deviceConfigurationId,
              configuration_name: currentDevice?.deviceConfigurationName,
            };
            break;
          case "GROUP_ID_VALIDATION":
            actionData.params = {
              group_id: groupId ?? 1,
              school_id: schoolId ?? 1,
              school_name: schoolName,
            };
            break;
          case "APP_INSTALLATION_CHECK":
            actionData.params = {
              required_packages: [taPackageName],
            };
            break;
          case "SD_CARD_SIZE_VALIDATION":
            actionData.params = {
              sd_card_size: currentDevice.sdCardSize,
            };
            break;
          case "SD_CARD_PROFILE_VALIDATION":
            actionData.params = {
              profile_name: currentDevice.sdCardProfileName,
              product_type: productType,
            };
            break;
          case "LOGIN_TO_TA_APP":
            actionData.params = {
              login_id: loginId,
              password: password,
              isAutoOneClickEnabled: true,
              executionTime: 10800000,
              wareHouseTesting: true,
              isClearDataEnabled: true,
              package_name: taPackageName,
            };
            break;
          default:
            break;
        }
        return actionData;
      }
    );
    /// check order
    return finalStatusMap;
  };

  const runQCTrigger = async (deviceId, deviceToken) => {
    let actions = getQCValidationAction(deviceId);
    const actionBody = {
      orderId: orderId,
      deviceId: deviceId,
      actions: actions,
    };
    if (deviceToken.length != 0) {
      try {
        const result = await triggerAction(
          deviceToken,
          null,
          ActionCommands.QC_CHECK,
          PROJECTS.TPS,
          actionBody
        );
        return result;
      } catch (err) {
        console.log("trigger error", err);
      }
    }
  };

  const handledActionFailed = (deviceId) => {
    const currentDevice = devices[deviceId];
    setDeviceTriggerStatus((prevDevices) => {
      const updatedDevices = { ...prevDevices };
      updatedDevices[deviceId] = {
        ...currentDevice[deviceId],
        triggerStatus: false,
      };
      return updatedDevices;
    });

    /// remove from list so fetch will ignore refresh
    const newArray = hardwaresForStatusRefresh.filter(
      (item) => item !== deviceId
    );
    setHardwaresForStatusRefresh([...newArray]);
  };

  const handledActionComplete = (deviceId) => {
    /// removed trigger info
    const currentDevice = devices[deviceId];
    setDeviceTriggerStatus((prevDevices) => {
      const updatedDevices = { ...prevDevices };
      updatedDevices[deviceId] = {
        ...currentDevice[deviceId],
        triggerStatus: false,
        isSaved: true,
      };
      return updatedDevices;
    });

    /// remove from list so fetch will ignore refresh
    const newArray = hardwaresForStatusRefresh.filter(
      (item) => item !== deviceId
    );
    setHardwaresForStatusRefresh([...newArray]);
  };

  const fetchHardwaresRefreshedData = () => {
    hardwaresForStatusRefreshRef.current.forEach((deviceId) => {
      setTimeout(() => {
        fetchLatestDeviceActions(deviceId).then((response) => {
          if (response && response.status === "OK") {
            setHardwaresQCStatus((prevStatus) => {
              const updatedStatus = { ...prevStatus };
              updatedStatus[deviceId] = response?.data || [];
              return updatedStatus;
            });
          }
        });
      }, 1000);
    });
  };

  const handleToastClose = () => {
    setStatusMessage(null);
  };

  const handleUploadAllYamls = async (acceptedFiles, devices) => {
    const maxCount = devices.length;

    let yamlFiles = acceptedFiles;
    if (acceptedFiles.length > 0) {
      yamlFiles = acceptedFiles.filter((file) => file.path.endsWith(".yaml"));
    }

    if (yamlFiles.length === 0) {
      setStatusMessage(OPP_CONSTANTS.messages.invalidYaml);
      return;
    }

    if (yamlFiles.length < maxCount) {
      setStatusMessage(
        OPP_CONSTANTS.messages.partialYamlUpload.replace(
          /{max_count}/,
          maxCount
        )
      );
      return;
    }
    if (yamlFiles.length > maxCount) {
      setStatusMessage(
        OPP_CONSTANTS.messages.exceedYamllimit.replace(/{max_count}/, maxCount)
      );
      return;
    }

    // Only handleYamlDrop called for individual yaml drop
    // To fill and basic perform basic validations
    for (let index = 0; index < yamlFiles.length; index++) {
      const file = yamlFiles[index];
      await handleYamlValidations([file], devices[index]);
    }

    //To Proceed with BE Triggers
    for (let index = 0; index < yamlFiles.length; index++) {
      const file = yamlFiles[index];
      await handleYamlDrop([file], devices[index]);
    }
  };

  const handlePenDriveUploadAllYamls = async (acceptedFiles, devices) => {
    const maxCount = devices.length;

    let yamlFiles = acceptedFiles;
    if (acceptedFiles.length > 0) {
      yamlFiles = acceptedFiles.filter((file) => file.path.endsWith(".yaml"));
    }

    if (yamlFiles.length === 0) {
      setStatusMessage(OPP_CONSTANTS.messages.invalidYaml);
      return;
    }

    if (yamlFiles.length < maxCount) {
      setStatusMessage(
        OPP_CONSTANTS.messages.partialYamlUpload.replace(
          /{max_count}/,
          maxCount
        )
      );
      return;
    }
    if (yamlFiles.length > maxCount) {
      setStatusMessage(
        OPP_CONSTANTS.messages.exceedYamllimit.replace(/{max_count}/, maxCount)
      );
      return;
    }

    // Only handleYamlDrop called for individual yaml drop
    // To fill and basic perform basic validations
    for (let index = 0; index < yamlFiles.length; index++) {
      const file = yamlFiles[index];
      await handlePenDriveYamlValidations([file], devices[index]);
    }

    //To Proceed with BE Triggers
    for (let index = 0; index < yamlFiles.length; index++) {
      const file = yamlFiles[index];
      await handlePenDriveYamlDrop([file], devices[index]);
    }
  };

  const handleYamlValidations = (acceptedFiles, currentDevice) => {
    return new Promise((resolve, reject) => {
      let yamlFiles = acceptedFiles;
      if (acceptedFiles.length > 0) {
        yamlFiles = acceptedFiles.filter((file) => file.path.endsWith(".yaml"));
      }
      if (yamlFiles.length === 0) {
        //Set SD card error message for selected device
        setDeviceTriggerStatus((prevDevices) => {
          const updatedDevices = { ...prevDevices };
          updatedDevices[currentDevice?.id] = {
            ...currentDevice[currentDevice?.id],
            triggerStatus: false,
            errorMessage: OPP_CONSTANTS.messages.invalidYaml,
          };
          return updatedDevices;
        });
        resolve(false);
        return;
      }

      // Clear SD card error message for selected device
      setDeviceTriggerStatus((prevDevices) => {
        const updatedDevices = { ...prevDevices };
        updatedDevices[currentDevice?.id] = {
          ...currentDevice[currentDevice?.id],
          triggerStatus: true,
          errorMessage: null,
        };
        return updatedDevices;
      });
      resolve(true);
    });
  };

  const handlePenDriveYamlValidations = (acceptedFiles, currentDevice) => {
    return new Promise((resolve, reject) => {
      let yamlFiles = acceptedFiles;
      if (acceptedFiles.length > 0) {
        yamlFiles = acceptedFiles.filter((file) => file.path.endsWith(".yaml"));
      }
      if (yamlFiles.length === 0) {
        setDeviceTriggerStatus((prevDevices) => {
          const updatedDevices = { ...prevDevices };
          updatedDevices[currentDevice?.id] = {
            ...currentDevice[currentDevice?.id],
            triggerStatus: false,
            errorMessage: OPP_CONSTANTS.messages.invalidYaml,
          };
          return updatedDevices;
        });
        resolve(false);
        return;
      }

      setDeviceTriggerStatus((prevDevices) => {
        const updatedDevices = { ...prevDevices };
        updatedDevices[currentDevice?.id] = {
          ...currentDevice[currentDevice?.id],
          triggerStatus: true,
          errorMessage: null,
        };
        return updatedDevices;
      });
      resolve(true);
    });
  };

  const handleYamlDrop = (acceptedFiles, currentDevice) => {
    return new Promise((resolve, reject) => {
      let yamlFiles = acceptedFiles;
      if (acceptedFiles.length > 0) {
        yamlFiles = acceptedFiles.filter((file) => file.path.endsWith(".yaml"));
      }

      if (yamlFiles.length === 0) {
        resolve(false);
        return;
      }

      const reader = new FileReader();

      reader.onload = async (event) => {
        const yamlFileContents = event.target.result;
        const yamlData = yaml.load(yamlFileContents);
        const fileResponse = validateYamlFile(yamlData, currentDevice);

        if (fileResponse && fileResponse?.status === "OK") {
          const deviceWithSerialId = Object.keys(devices)?.filter((key) => {
            return (
              currentDevice.id !== devices[key]?.id &&
              devices[key]?.deviceNo === fileResponse?.id
            );
          });

          if (deviceWithSerialId?.length > 0) {
            setDeviceTriggerStatus((prevDevices) => {
              const updatedDevices = { ...prevDevices };
              updatedDevices[currentDevice?.id] = {
                ...currentDevice[currentDevice?.id],
                triggerStatus: false,
                errorMessage: OPP_CONSTANTS.messages.invalidSerialId,
              };
              return updatedDevices;
            });
            handleSerialIdChange(currentDevice?.id, fileResponse.id);
            ///Finished Due to duplicate id found
            resolve(false);
            return;
          } else {
            handleSerialIdChange(currentDevice?.id, fileResponse.id);
          }

          const deviceId = currentDevice?.id;
          /// reset data
          setHardwaresQCStatus((prevStatus) => {
            const updatedStatus = { ...prevStatus };
            delete updatedStatus[deviceId];
            return updatedStatus;
          });

          /// set state as its triggered
          setDeviceTriggerStatus((prevDevices) => {
            const updatedDevices = { ...prevDevices };
            updatedDevices[deviceId] = {
              ...currentDevice[deviceId],
              triggerStatus: true,
              errorMessage: null,
            };
            return updatedDevices;
          });

          const registerDevice = await updateDeviceToTps(
            deviceId,
            fileResponse.id
          );

          if (!registerDevice) {
            /// Failed to registed with TPS
            resolve(false);
            return;
          }

          let sdCardActionData = {
            actionId: "SD_CARD_PROFILE_VALIDATION",
            status: fileResponse.qc_Status
              ? DEVICE_STATUS.SUCCESS.toUpperCase()
              : DEVICE_STATUS.FAILURE.toUpperCase(),
            deviceStatus: fileResponse.qc_Status
              ? DEVICE_STATUS.SUCCESS.toUpperCase()
              : DEVICE_STATUS.FAILURE.toUpperCase(),
            sdCardSerialId: fileResponse.id,
            summary: fileResponse.message,
            additionalInfo: JSON.stringify({ ...yamlData }),
          };

          const actionResult = await setDeviceActionToTPS(
            orderId,
            fileResponse.id,
            sdCardActionData
          );

          if (actionResult && actionResult.status === "OK") {
            ///Add to que list for refreshing the data from server
            if (!hardwaresForStatusRefreshRef.current.includes(deviceId)) {
              setHardwaresForStatusRefresh([
                ...hardwaresForStatusRefreshRef.current,
                deviceId,
              ]);
            }
            let displayActionData = {
              actionId: "DISPLAY_MESSAGE",
              status: fileResponse.qc_Status
                ? DEVICE_STATUS.SUCCESS.toUpperCase()
                : DEVICE_STATUS.FAILURE.toUpperCase(),
              deviceStatus: fileResponse.qc_Status
                ? DEVICE_STATUS.SUCCESS.toUpperCase()
                : DEVICE_STATUS.FAILURE.toUpperCase(),
              summary: fileResponse.message,
            };
            const displayActionResponse = await setDeviceActionToTPS(
              orderId,
              fileResponse.id,
              displayActionData
            );

            // Refresh of Status will work once in each 2 seconds
            // this might be failed in filed for more than couple of SD cards triggers
            setTimeout(() => {
              if (
                displayActionResponse &&
                displayActionResponse.status === "OK"
              ) {
                handledActionComplete(deviceId);
              } else {
                handledActionFailed(deviceId);
              }
              resolve(true);
            }, 3000);
          } else {
            setDeviceTriggerStatus((prevDevices) => {
              const updatedDevices = { ...prevDevices };
              updatedDevices[currentDevice?.id] = {
                ...currentDevice[currentDevice?.id],
                triggerStatus: false,
                errorMessage:
                  actionResult?.message ??
                  OPP_CONSTANTS.messages.failToSubmitAction,
              };
              return updatedDevices;
            });
            resolve(false);
          }
        } else {
          handleSerialIdChange(currentDevice?.id, "");
          setDeviceTriggerStatus((prevDevices) => {
            const updatedDevices = { ...prevDevices };
            updatedDevices[currentDevice?.id] = {
              ...currentDevice[currentDevice?.id],
              triggerStatus: false,
              errorMessage:
                fileResponse?.message ?? OPP_CONSTANTS.messages.invalidYaml,
            };
            return updatedDevices;
          });
          resolve(false);
        }
      };

      reader.onerror = () => reject(false);
      reader.readAsText(yamlFiles[0]);
    });
  };

  const handlePenDriveYamlDrop = (acceptedFiles, currentDevice) => {
    return new Promise((resolve, reject) => {
      let yamlFiles = acceptedFiles;
      if (acceptedFiles.length > 0) {
        yamlFiles = acceptedFiles.filter((file) => file.path.endsWith(".yaml"));
      }

      if (yamlFiles.length === 0) {
        resolve(false);
        return;
      }

      const reader = new FileReader();

      reader.onload = async (event) => {
        const yamlFileContents = event.target.result;
        const yamlData = yaml.load(yamlFileContents);
        const fileResponse = validatePenDriveYamlFile(yamlData, currentDevice);

        if (fileResponse && fileResponse?.status === "OK") {
          const deviceWithSerialId = Object.keys(devices)?.filter((key) => {
            return (
              currentDevice.id !== devices[key]?.id &&
              devices[key]?.deviceNo === fileResponse?.id
            );
          });

          if (deviceWithSerialId?.length > 0) {
            setDeviceTriggerStatus((prevDevices) => {
              const updatedDevices = { ...prevDevices };
              updatedDevices[currentDevice?.id] = {
                ...currentDevice[currentDevice?.id],
                triggerStatus: false,
                errorMessage: OPP_CONSTANTS.messages.invalidSerialId,
              };
              return updatedDevices;
            });
            resolve(false);
            return;
          }

          const deviceId = currentDevice?.id;
          /// reset data
          setHardwaresQCStatus((prevStatus) => {
            const updatedStatus = { ...prevStatus };
            delete updatedStatus[deviceId];
            return updatedStatus;
          });

          /// set state as its triggered
          setDeviceTriggerStatus((prevDevices) => {
            const updatedDevices = { ...prevDevices };
            updatedDevices[deviceId] = {
              ...currentDevice[deviceId],
              triggerStatus: true,
              errorMessage: null,
            };
            return updatedDevices;
          });

          let sdCardActionData = {
            actionId: "PEN_DRIVE_YAML_VALIDATION",
            status: fileResponse.qc_Status
              ? DEVICE_STATUS.SUCCESS.toUpperCase()
              : DEVICE_STATUS.FAILURE.toUpperCase(),
            deviceStatus: fileResponse.qc_Status
              ? DEVICE_STATUS.SUCCESS.toUpperCase()
              : DEVICE_STATUS.FAILURE.toUpperCase(),
            sdCardSerialId: fileResponse.id,
            summary: fileResponse.message,
            additionalInfo: JSON.stringify({ ...yamlData }),
          };

          const actionResult = await setDeviceActionToTPS(
            orderId,
            fileResponse.id,
            sdCardActionData
          );

          if (actionResult && actionResult.status === "OK") {
            ///Add to que list for refreshing the data from server
            if (!hardwaresForStatusRefreshRef.current.includes(deviceId)) {
              setHardwaresForStatusRefresh([
                ...hardwaresForStatusRefreshRef.current,
                deviceId,
              ]);
            }
            let displayActionData = {
              actionId: "DISPLAY_MESSAGE",
              status: fileResponse.qc_Status
                ? DEVICE_STATUS.SUCCESS.toUpperCase()
                : DEVICE_STATUS.FAILURE.toUpperCase(),
              deviceStatus: fileResponse.qc_Status
                ? DEVICE_STATUS.SUCCESS.toUpperCase()
                : DEVICE_STATUS.FAILURE.toUpperCase(),
              summary: fileResponse.message,
            };
            const displayActionResponse = await setDeviceActionToTPS(
              orderId,
              fileResponse.id,
              displayActionData
            );

            // Refresh of Status will work once in each 2 seconds
            // this might be failed in filed for more than couple of SD cards triggers
            setTimeout(() => {
              if (
                displayActionResponse &&
                displayActionResponse.status === "OK"
              ) {
                handledActionComplete(deviceId);
              } else {
                handledActionFailed(deviceId);
              }
              resolve(true);
            }, 3000);
          } else {
            setDeviceTriggerStatus((prevDevices) => {
              const updatedDevices = { ...prevDevices };
              updatedDevices[currentDevice?.id] = {
                ...currentDevice[currentDevice?.id],
                triggerStatus: false,
                errorMessage:
                  actionResult?.message ??
                  OPP_CONSTANTS.messages.failToSubmitAction,
              };
              return updatedDevices;
            });
            resolve(false);
          }
        } else {
          setDeviceTriggerStatus((prevDevices) => {
            const updatedDevices = { ...prevDevices };
            updatedDevices[currentDevice?.id] = {
              ...currentDevice[currentDevice?.id],
              triggerStatus: false,
              errorMessage:
                fileResponse?.message ?? OPP_CONSTANTS.messages.invalidYaml,
            };
            return updatedDevices;
          });
          resolve(false);
        }
      };

      reader.onerror = () => reject(false);
      reader.readAsText(yamlFiles[0]);
    });
  };

  const handleTVSerialIDSubmit = async (deviceId) => {
    const currentDevice = devices[deviceId];

    /// set state as its triggered
    setDeviceTriggerStatus((prevDevices) => {
      const updatedDevices = { ...prevDevices };
      updatedDevices[deviceId] = {
        ...currentDevice[deviceId],
        triggerStatus: true,
        errorMessage: null,
      };
      return updatedDevices;
    });

    const registerDevice = await updateDeviceToTps(
      deviceId,
      currentDevice?.deviceNo
    );

    if (registerDevice) {
      handledActionComplete(deviceId);
    }
    return;
  };

  const validateYamlFile = (yamlData, currentDevice) => {
    let message = null;
    if (!yamlData) {
      return {
        id: null,
        status: "ERROR",
        message: OPP_CONSTANTS.messages.invalidYaml,
      };
    }

    const expectedProfile = currentDevice.sdCardProfileName ?? "";
    const detectedProfile = yamlData.sd_card_version ?? "";

    let isSameProfile = false;
    if (detectedProfile) {
      const profileParts = detectedProfile.split("-");
      if (profileParts && profileParts.length > 0) {
        isSameProfile =
          expectedProfile.length > 0 &&
          expectedProfile.startsWith(profileParts[0]);
      }
    }

    const isSameSnapshot =
      expectedProfile.length > 0 &&
      detectedProfile.startsWith(expectedProfile);

    const isDateValid =
      moment.utc(yamlData.burn_date).local() >=
      moment.utc(yamlData.verification_date).local();

    const isQcResultValid =
      yamlData.burn_qcresult && yamlData.verification_qcresult;

    const isRedCountValid =
      yamlData.burn_red === "0" && yamlData.verification_red === "0";

    if (isSameProfile && isDateValid) {
      return {
        id: yamlData.sd_card_serial_number,
        status: "OK",
        qc_Status:
          isSameSnapshot && isDateValid && isQcResultValid && isRedCountValid,
        message: `Expected Profile: <b>${currentDevice.sdCardProfileName}</b>.
        <br />The Detected Profile: <b>${yamlData.sd_card_version ?? "invalid"
          }</b>.        
        <br />Burn Date: <b>${yamlData.burn_date}</b>,
        <br />Verification Date:<b>${yamlData.verification_date}</b>
        <br />Burn Red Resources count:<b>${yamlData.burn_red}</b>,
        <br />Verification Red Resources count:<b>${yamlData.verification_red
          }</b>
        <br />QC State is <b>${isQcResultValid}</b>
        `,
      };
    } else {
      if (!isSameProfile) {
        message = `SD Profiles not matched!
        <br /> Expected Profile: <b>${currentDevice.sdCardProfileName}</b>.
        <br />The Detected Profile: <b>${yamlData.sd_card_version ?? "invalid"
          }</b>.`;
      } else if (!isQcResultValid) {
        message = `Profile QC is failed!
        <br />QC State is <b>${isQcResultValid}</b>`;
      } else if (!isDateValid) {
        message = `Profile QC is failed! Not a valid dates      
        <br />Burn Date: <b>${yamlData.burn_date}</b>,<br />Verification Date:<b>${yamlData.verification_date}</b>`;
      } else if (!isRedCountValid) {
        message = `Red Resources in SD card!
        <br />Burn Red Resources count:<b>${yamlData.burn_red}</b>,
        <br />Verification Red Resources count:<b>${yamlData.verification_red}</b>`;
      }

      return {
        id: null,
        status: "ERROR",
        qc_Status: false,
        message:
          message ??
          `Expected Profile: <b>${currentDevice.sdCardProfileName}</b>.
        <br />The Detected Profile: <b>${yamlData.sd_card_version ?? "invalid"
          }</b>.        
        <br />Burn Date: <b>${yamlData.burn_date}</b>, Verification Date:<b>${yamlData.burn_date
          }</b>
        <br />Burn Red Resources count:<b>${yamlData.verification_red
          }</b>, Verification Red Resources count:<b>${yamlData.verification_red
          }</b>
        <br />QC State is <b>${isQcResultValid}</b>
        `,
      };
    }
  };

  const validatePenDriveYamlFile = (yamlData, currentDevice) => {
    let message = null;
    if (!yamlData) {
      return {
        id: null,
        status: "ERROR",
        message: OPP_CONSTANTS.messages.invalidYaml,
      };
    }

    let hasError = false

    if (!currentDevice.deviceNo || currentDevice.deviceNo.trim().length == 0) {
      hasError = true
      message = 'Device ID missing! Please enter device ID'
    } else if (!yamlData['burn_time']) {
      hasError = true
      message = `Missing required key: burn_time`
    } else if (!yamlData['hostname']) {
      hasError = true
      message = `Missing required key: hostname`
    } else if (!yamlData['burn_user']) {
      hasError = true
      message = `Missing required key: burn_user`
    } else if (!yamlData['files'] || !Array.isArray(yamlData.files) || yamlData.files.length === 0) {
      hasError = true
      message = `Missing required key: files or file list is empty`
    }

    if (!hasError) {
      return {
        id: currentDevice.deviceNo,
        status: "OK",
        qc_Status: true,
        message: `Burn Time: <b>${yamlData.burn_time}</b>
        <br />Host Name: <b>${yamlData.hostname}</b> 
        <br />Burn User: <b>${yamlData.burn_user}</b>
        <br />File count:<b>${yamlData.files.length}</b>`,
      };
    }

    return {
      id: null,
      status: "ERROR",
      qc_Status: false,
      message
    };
  };

  const cardDetailsLayout = (
    hardwareModel,
    hardwareType,
    count,
    sdProfile,
    sdCardSize,
    firstDevice,
    stockLocationLoading,
    setStockLocationLoadingForCard
  ) => {
    const isTechbook = firstDevice?.isTechbook
    const uniqueKey = `${productType}-${hardwareModel}-${hardwareType}-${isTechbook}`;

    const stockLocation = selectedStockLocations[uniqueKey];
    const handleStockLocationSelect = async (loc) => {
      // Only trigger if the value is actually changing
      if (selectedStockLocations[uniqueKey] === loc) {
        setStockLocationModal({ open: false, cardKey: null });
        return;
      }
      setStockLocationLoadingForCard(true);

      // Find all devices matching this card
      const devicesToUpdate = Object.values(devices || {}).filter(
        (d) =>
          d.deviceModel === hardwareModel &&
          d.deviceType === hardwareType &&
          (d.isTechbook === isTechbook || !('isTechbook' in d && isTechbook))
      );
      if (!devicesToUpdate.length) {
        setStockLocationLoadingForCard(false);
        setStockLocationModal({ open: false, cardKey: null });
        return;
      }

      // Prepare request body for updateOrderDevices
      const updatePayload = devicesToUpdate.map((d) => ({
        id: d.id,
        stockLocation: loc,
      }));
      try {
        const response = await updateOrderDevices(orderId, { devices: updatePayload });
        if (response && response.status === 'OK') {
          setSelectedStockLocations((prev) => ({ ...prev, [uniqueKey]: loc }));
        } else {
          setStatusMessage(response?.message || 'Failed to update stock location.');
        }
      } catch (error) {
        setStatusMessage('Network error while updating stock location.');
      } finally {
        setStockLocationLoadingForCard(false);
        setStockLocationModal({ open: false, cardKey: null });
      }
    };

    const openStockLocationModal = () => setStockLocationModal({ open: true, cardKey: uniqueKey });
    const closeStockLocationModal = () => setStockLocationModal({ open: false, cardKey: null });
    const hardwareConfig = HARDWARE_TYPE[hardwareType.toUpperCase()] || HARDWARE_TYPE.TAB;
    const iconType = hardwareConfig.type || "fa";
    const iconValue = hardwareConfig.icon;

    const configurationId = firstDevice?.deviceConfigurationId;
    const configurationName = firstDevice?.deviceConfigurationName;
    const enableQRModel =
      configurationId != null &&
      groupId != null &&
      Number.isFinite(Number(configurationId)) &&
      Number.isFinite(Number(groupId)) &&
      hardwareType.toUpperCase() === HARDWARE_TYPE.TAB.name.toUpperCase();

    const showUploadCTA =
      hardwareType.toUpperCase() === HARDWARE_TYPE.SDCARD.name.toUpperCase();

    return (
      <Row className="hardware-info-row">
        <Row className="hardware-info-row" style={{ justifyContent: "start" }}>
          {iconType === "fa" ? (
            <FontAwesomeIcon
              icon={iconValue}
              size="2x"
              style={{ marginRight: "10px" }}
            />
          ) : iconValue ? (
            (() => {
              const Icon = iconValue;
              return <Icon style={{ width: 25, height: 28, marginRight: 10 }} />;
            })()
          ) : null}
          <Col
            className="hardware-info-col"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div className="summary-value" style={{ color: "#fff" }}>
              {!!count ? count : "NA"}
            </div>
            <div className="summary-label" style={{ color: "#fff" }}>
              {hardwareModel} {hardwareType}
            </div>
          </Col>
          {firstDevice?.brand && (
            <Col
              className="hardware-info-col"
              style={{
                display: "flex",
                flexDirection: "column",
                marginLeft: "40px", // Optional spacing
              }}
            >
              <div className="summary-value" style={{ color: "#fff" }}>
                {firstDevice.brand}
              </div>
              <div className="summary-label" style={{ color: "#fff" }}>
                Brand
              </div>
            </Col>
          )}
        </Row>

        {sdProfile &&
          hardwareType.toUpperCase() !== HARDWARE_TYPE.TV.name.toUpperCase() &&
          hardwareType.toUpperCase() !== HARDWARE_TYPE.PANEL.name.toUpperCase() &&
          hardwareType.toUpperCase() !== HARDWARE_TYPE.PENDRIVE.name.toUpperCase() && (
            <Row className="hardware-info-row" style={{ justifyContent: "start" }}>
              <FontAwesomeIcon
                icon={HARDWARE_TYPE.SDCARD.icon}
                size="2x"
                style={{ marginRight: "10px" }}
              />
              <Col className="hardware-info-row">
                <div className="summary-value" style={{ color: "#fff" }}>
                  {sdProfile ? `${sdProfile}` : "NA"}
                </div>
                <div className="summary-label" style={{ color: "#fff" }}>
                  <b>{sdCardSize / 1024} GB </b>
                  {ORDER_DATA_SUMMARY.ORDER_DETAILS.profileName}
                </div>
              </Col>
            </Row>
          )}

        {/* Stock Location Feature */}
        <Row className="hardware-info-row" style={{ justifyContent: "start" }}>
          <Col className="hardware-info-col" style={{ display: "flex", flexDirection: "column" }}>
            <div className="summary-value" style={{ color: stockLocation ? "#FFD700" : "#fff", display: 'flex', alignItems: 'center', gap: 6, marginLeft: '15px' }}>
              {stockLocation ? (
                <>
                  <span>{stockLocation}</span>
                  {!disableAllActions && (
                    <button
                      style={{
                        background: '#FF7929', // light orange background
                        color: '#fff',
                        border: '2px solid #FF7929',
                        borderRadius: 4,
                        padding: '2px',
                        marginLeft: 4,
                        fontSize: 13,
                        cursor: 'pointer',
                        fontWeight: 600,
                        transition: 'background 0.2s',
                      }}
                      onClick={openStockLocationModal}
                      type="button"
                    >
                      Change
                    </button>
                  )}
                </>
              ) : (
                !disableAllActions && (
                  <button
                    style={{ background: '#fff', color: '#222', borderRadius: 4, border: 'none', padding: '2px 4px', cursor: 'pointer', fontWeight: 500 }}
                    onClick={openStockLocationModal}
                    type="button"
                  >
                    Select Location
                  </button>
                )
              )}
            </div>
            <div className="summary-label" style={{ color: "#fff", fontSize: '1em' }}>
              Stock Location
            </div>
            <Modal
              title="Select Stock Location"
              open={stockLocationModal.open && stockLocationModal.cardKey === uniqueKey}
              onCancel={closeStockLocationModal}
              footer={null}
              centered
            >
              {stockLocationLoadingMap[uniqueKey] ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <Spin size="large" />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {STOCK_LOCATIONS.map((loc) => (
                    <button
                      key={loc}
                      style={{
                        background: stockLocation === loc ? '#FF7929' : '#f0f0f0',
                        color: stockLocation === loc ? '#fff' : '#222',
                        border: 'none',
                        borderRadius: 4,
                        padding: '7px 0',
                        fontWeight: 500,
                        cursor: stockLocationLoading ? 'not-allowed' : 'pointer',
                        opacity: stockLocationLoading ? 0.6 : 1,
                      }}
                      onClick={() => !stockLocationLoading && handleStockLocationSelect(loc)}
                      disabled={stockLocationLoading}
                    >
                      {loc}
                    </button>
                  ))}
                  <button
                    style={{
                      background: '#DC2626',
                      color: '#fff',
                      border: '1px dashed #bbb',
                      borderRadius: 4,
                      padding: '7px 0',
                      fontWeight: 500,
                      cursor: stockLocationLoading ? 'not-allowed' : 'pointer',
                      opacity: stockLocationLoading ? 0.6 : 1,
                      marginTop: 10,
                    }}
                    onClick={() => !stockLocationLoading && handleStockLocationSelect(null)}
                    disabled={stockLocationLoading}
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </Modal>
          </Col>
        </Row>

        {enableQRModel ? (
          <Row
            className="hardware-info-row"
            style={{ justifyContent: "end", marginRight: "10px" }}
            onClick={() => handleQRClick(uniqueKey)}
          >
            <QRCodeComponent
              iconColor={"#fff"}
              title={`${hardwareModel} ${hardwareType}: ${configurationName ?? ""}`}
              isModalOpen={isModalOpen === uniqueKey}
              config={configurationId}
              group={groupId}
              handleQRClick={() => handleQRClick(uniqueKey)}
            />
          </Row>
        ) : (
          <Row className="hardware-info-row" style={{ justifyContent: "end", marginRight: "10px" }}>
            {hardwareType.toUpperCase() === HARDWARE_TYPE.TAB.name.toUpperCase() && (
              <FontAwesomeIcon icon={"link-slash"} color={"white"} size="2x" />
            )}
          </Row>
        )}

        {showUploadCTA && (
          <Row className="hardware-info-row" style={{ justifyContent: "end", marginRight: "10px" }}>
            <div className="opp-button-container">
              <Dropzone
                onDrop={(e) =>
                  hardwareType.toUpperCase() === HARDWARE_TYPE.PENDRIVE.name.toUpperCase()
                    ? handlePenDriveUploadAllYamls(e, deviceMapData[hardwareModel])
                    : handleUploadAllYamls(e, deviceMapData[hardwareModel])
                }
                multiple={true}
                accept=".yaml, .yml"
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()}>
                    <input {...getInputProps({ accept: ".yaml, .yml" })} />
                    <TriggerComponent
                      disabled={
                        disableAllActions ||
                        (hardwareType.toUpperCase() === HARDWARE_TYPE.SDCARD.name.toUpperCase() &&
                          enabledSDCardCount > 0)
                      }
                      title={OPP_CONSTANTS.btns.uploadAllYamls}
                      icon={"fa-upload"}
                    />
                  </div>
                )}
              </Dropzone>
            </div>
          </Row>
        )}
      </Row>
    );
  };

  const InputTextSerialId = (currentDevice, className) => {
    let isTriggered =
      deviceTriggerStatus?.[currentDevice?.id]?.triggerStatus ?? false;
    let ignoreUnique = false
    return (
      <div className={`editable-container ${className}`}>
        <EditableComponent
          ignoreUnique={ignoreUnique}
          focusKey={currentDevice?.id}
          orderData={devices}
          serialId={currentDevice?.deviceNo}
          disabled={disableAllActions || isTriggered}
          handleOnChange={(s, error) => {
            handleSerialIdChange(currentDevice?.id, s, error);
          }}
          createFormRef={(ref) => {
            Object.keys(devices)?.map((key, index) => {
              if (key === "" + currentDevice?.id) {
                inputRefs.current[index] = ref;
              }
            });
          }}
          errorMessage={deviceTriggerStatus?.[currentDevice?.id]?.errorMessage}
        />
      </div>
    );
  };

  const installationReportLink = (currentDevice) => {
    const reportData = installationReports[currentDevice?.id];
    const deviceType = currentDevice?.deviceType?.toUpperCase();
    const isIFPorTV = deviceType === HARDWARE_TYPE.TV.name.toUpperCase() || 
                      deviceType === HARDWARE_TYPE.IFP?.name?.toUpperCase();
    
    if (!isIFPorTV || !reportData) {
      return null;
    }

    return (
      <div className="installation-report-link-container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 10px'
      }}>
        <a
          href={`/#/report-view/${reportData.reportId}`}
          target="_blank"
          rel="noopener noreferrer"
          title={`Installation Report - ${reportData.salesOrderNo}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textDecoration: 'none',
            color: '#52c41a'
          }}
        >
          <FontAwesomeIcon
            icon="check-circle"
            size="2x"
            style={{ color: '#52c41a', marginBottom: '4px' }}
          />
          <span style={{ fontSize: '10px', fontWeight: 'bold' }}>Setup Done</span>
        </a>
      </div>
    );
  };

  const triggerButton = (currentDevice, isValidNo, isTriggerConfirmed) => {
    const nucleusValid = oneNucleusValid;
    const isTabDevice =
      currentDevice?.deviceType?.toUpperCase() ===
      HARDWARE_TYPE.TAB.name.toUpperCase();
    const isTriggerBlockedForTab = isTabDevice && nucleusValid === false;

    let isDisabled = !isValidNo || isTriggerBlockedForTab;
    let isTriggered =
      deviceTriggerStatus?.[currentDevice?.id]?.triggerStatus ?? false;
    return (
      <div className="device-trigger-container">
        <TriggerComponent
          disabled={disableAllActions || isDisabled}
          title={
            isTriggered && isTriggerConfirmed ? OPP_CONSTANTS.btns.stopQC : OPP_CONSTANTS.btns.start
          }
          bgColor={isTriggered && isTriggerConfirmed ? "red" : null}
          handleSubmit={() =>
            isTriggered && isTriggerConfirmed
              ? cancelQC(currentDevice?.id)
              : handleQCSubmit(currentDevice?.id)
          }
        />
      </div>
    );
  };

  const tabDetailedLayout = (currentDevice, isValidNo) => {
    let iconName = `${PRODUCT_THEME[productType]?.icon ?? PRODUCT_THEME.LEAD.icon
      }`;
    if (currentDevice?.isTechbook) {
      iconName = `${PRODUCT_THEME.LEAD_TECHBOOK.icon}`;
    }
    let isTriggered =
      deviceTriggerStatus?.[currentDevice?.id]?.triggerStatus ?? false;

    // check if trigger is confirmed for showing stop QC option
    const isTriggerConfirmed = confirmedDevices?.[currentDevice?.id] ?? false;

    return (
      <Card
        size="small"
        hoverable
        style={{
          marginBottom: "10px",
        }}
        bodyStyle={{
          borderRadius: "6px",
          padding: 0,
          backgroundColor: "#ff5f0011",
        }}
      >
        <Row key={`index_cdc_${currentDevice?.id}`}>
          <div
            className="device-detail-container"
            style={{
              borderRight: `1px solid #ff5f00`,
            }}
          >
            <img className="product-logo" src={iconName}></img>
            {InputTextSerialId(currentDevice)}
            {installationReportLink(currentDevice)}

            <StatusAnimationComponent
              isQCRunning={hardwaresForStatusRefresh.includes(
                currentDevice?.id
              )}
              currentDevice={currentDevice?.id}
              isTriggered={isTriggered && isTriggerConfirmed}
              deviceDefaultStatus={deviceActionStatus[currentDevice?.id] ?? []}
              deviceStatus={hardwaresQCStatus[currentDevice?.id]}
              handledActionFailed={() => handledActionFailed(currentDevice?.id)}
              handledActionComplete={() =>
                handledActionComplete(currentDevice?.id)
              }
            />

            {triggerButton(currentDevice, isValidNo, isTriggerConfirmed)}
            <Modal
              title="Existing Orders Detected"
              open={triggerModal.visible}
              onOk={handleConfirmTrigger}
              onCancel={() => setTriggerModal({ visible: false, mdmDeviceId: null, deviceId: null, reason: "", existingOrders: [] })}
              okText="Confirm Trigger"
              // mask={false}
              maskStyle={{
                backgroundColor: "rgba(0, 0, 0, 0.1)", // semi-transparent black
                backdropFilter: "blur(3px)"           // optional: adds blur
              }}
              okButtonProps={{ danger: false }}
              className="trigger-reason-modal"
            >
              <p>Existing Orders found for this device:</p>
              <ul style={{ paddingLeft: 20 }}>
                {triggerModal.existingOrders.map((order, idx) => (
                  <li key={idx}>{order.orpOrderId || JSON.stringify(order)}</li>
                ))}
              </ul>

              <p>Enter a reason to trigger anyway:</p>
              <Input.TextArea
                rows={3}
                value={triggerModal.reason}
                onChange={(e) => setTriggerModal({ ...triggerModal, reason: e.target.value })}
                placeholder="Reason for triggering despite existing orders"
              />
            </Modal>
          </div>
          <CertificateComponent
            isAtDevice={true}
            isForSDCard={false}
            deviceDefaultStatus={deviceActionStatus[currentDevice?.id] ?? []}
            deviceStatus={hardwaresQCStatus[currentDevice?.id]}
          />
        </Row>
      </Card >
    );
  };

  const tvDetailedLayout = (currentDevice, isValidNo) => {
    let iconName = `${PRODUCT_THEME[productType]?.icon ?? PRODUCT_THEME.LEAD.icon
      }`;

    let isTriggered =
      deviceTriggerStatus?.[currentDevice?.id]?.triggerStatus ?? false;
    let isSaved = deviceTriggerStatus?.[currentDevice?.id]?.isSaved ?? false;

    let ctaTitle = isTriggered
      ? OPP_CONSTANTS.btns.updating
      : currentDevice.isSaved || isSaved
        ? OPP_CONSTANTS.btns.update
        : OPP_CONSTANTS.btns.save;

    return (
      <Card
        size="small"
        hoverable
        style={{
          marginBottom: "10px",
        }}
        bodyStyle={{
          borderRadius: "6px",
          padding: 0,
          backgroundColor: "#ff5f0011",
        }}
      >
        <Row key={`index_cdc_${currentDevice?.id}`}>
          <div
            className="device-detail-container"
            style={{
              borderRight: `1px solid #ff5f00`,
            }}
          >
            <img className="product-logo" src={iconName}></img>
            {InputTextSerialId(currentDevice, "tv")}
            {installationReportLink(currentDevice)}
            <div
              className="device-trigger-container"
              style={{ justifyContent: "flex-end", flex: 1 }}
            >
              <TriggerComponent
                disabled={!isValidNo || disableAllActions || isTriggered}
                title={ctaTitle}
                handleSubmit={() =>
                  isTriggered ? {} : handleTVSerialIDSubmit(currentDevice.id)
                }
              />
            </div>
          </div>
          <div className="align-center">
            <FontAwesomeIcon
              icon={"circle-check"}
              size="2x"
              style={{ marginRight: "10px" }}
              color={currentDevice.isSaved || isSaved ? "green" : "gray"}
            />
          </div>
        </Row>
      </Card>
    );
  };

  const panelDetailedLayout = (currentDevice, isValidNo) => {
    let iconName = `${PRODUCT_THEME[productType]?.icon ?? PRODUCT_THEME.LEAD.icon
      }`;

    let isTriggered =
      deviceTriggerStatus?.[currentDevice?.id]?.triggerStatus ?? false;
    let isSaved = deviceTriggerStatus?.[currentDevice?.id]?.isSaved ?? false;

    let ctaTitle = isTriggered
      ? OPP_CONSTANTS.btns.updating
      : currentDevice.isSaved || isSaved
        ? OPP_CONSTANTS.btns.update
        : OPP_CONSTANTS.btns.save;

    return (
      <Card
        size="small"
        hoverable
        style={{
          marginBottom: "10px",
        }}
        bodyStyle={{
          borderRadius: "6px",
          padding: 0,
          backgroundColor: "#ff5f0011",
        }}
      >
        <Row key={`index_cdc_${currentDevice?.id}`}>
          <div
            className="device-detail-container"
            style={{
              borderRight: `1px solid #ff5f00`,
            }}
          >
            <img className="product-logo" src={iconName}></img>
            {InputTextSerialId(currentDevice, "panel")}
            <div
              className="device-trigger-container"
              style={{ justifyContent: "flex-end", flex: 1 }}
            >
              <TriggerComponent
                disabled={!isValidNo || disableAllActions || isTriggered}
                title={ctaTitle}
                handleSubmit={() =>
                  isTriggered ? {} : handleTVSerialIDSubmit(currentDevice.id)
                }
              />
            </div>
          </div>
          <div className="align-center">
            <FontAwesomeIcon
              icon={"circle-check"}
              size="2x"
              style={{ marginRight: "10px" }}
              color={currentDevice.isSaved || isSaved ? "green" : "gray"}
            />
          </div>
        </Row>
      </Card>
    );
  };

  const penDriveDetailedLayout = (currentDevice, isValidNo) => {
    let iconName = `${PRODUCT_THEME[productType]?.icon ?? PRODUCT_THEME.LEAD.icon
      }`;

    let isTriggered =
      deviceTriggerStatus?.[currentDevice?.id]?.triggerStatus ?? false;
    let isSaved = deviceTriggerStatus?.[currentDevice?.id]?.isSaved ?? false;

    let ctaTitle = isTriggered
      ? OPP_CONSTANTS.btns.updating
      : currentDevice.isSaved || isSaved
        ? OPP_CONSTANTS.btns.update
        : OPP_CONSTANTS.btns.save;

    return (
      <Card
        size="small"
        hoverable
        style={{
          marginBottom: "10px",
        }}
        bodyStyle={{
          borderRadius: "6px",
          padding: 0,
          backgroundColor: "#ff5f0011",
        }}
      >
        <Row key={`index_cdc_${currentDevice?.id}`}>
          <div
            className="device-detail-container"
            style={{
              borderRight: `1px solid #ff5f00`,
            }}
          >
            <img className="product-logo" src={iconName}></img>
            {InputTextSerialId(currentDevice, "panel")}
            <div
              className="device-trigger-container"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "650px",
                flex: 1,
              }}
            >
              <TriggerComponent
                disabled={!isValidNo || disableAllActions || isTriggered}
                title={ctaTitle}
                handleSubmit={() =>
                  isTriggered ? {} : handleTVSerialIDSubmit(currentDevice.id)
                }
              />
              <Dropzone
                onDrop={async (e) => {
                  await handlePenDriveYamlValidations(e, currentDevice);
                  await handlePenDriveYamlDrop(e, currentDevice);
                }}
                multiple={false}
                accept=".yaml, .yml"
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()}>
                    <input {...getInputProps({ accept: ".yaml, .yml" })} />

                    <TriggerComponent
                      disabled={disableAllActions || isTriggered}
                      title={
                        isTriggered
                          ? OPP_CONSTANTS.btns.validatingYaml
                          : OPP_CONSTANTS.btns.uploadYaml
                      }
                      icon={"fa-upload"}
                    />
                  </div>
                )}
              </Dropzone>
            </div>
          </div>
          <div className="align-center">
            <FontAwesomeIcon
              icon={"circle-check"}
              size="2x"
              style={{ marginRight: "10px" }}
              color={currentDevice.isSaved || isSaved ? "green" : "gray"}
            />
          </div>
        </Row>
      </Card>
    );
  };

  const sdCardDetailedLayout = (currentDevice) => {
    let iconName = `${PRODUCT_THEME[productType]?.icon ?? PRODUCT_THEME.LEAD.icon
      }`;
    let isTriggered =
      deviceTriggerStatus?.[currentDevice?.id]?.triggerStatus ?? false;
    return (
      <Card
        size="small"
        hoverable
        style={{
          marginBottom: "10px",
        }}
        bodyStyle={{
          borderRadius: "6px",
          padding: 0,
          backgroundColor: "#ff5f0011",
        }}
      >
        <Row key={`index_cdc_${currentDevice?.id}`}>
          <div
            className="device-detail-container"
            style={{
              borderRight: `1px solid #ff5f00`,
            }}
          >
            <img className="product-logo" src={iconName}></img>
            {InputTextSerialId(currentDevice, "tv")}
            <div
              className="device-trigger-container"
              style={{ justifyContent: "flex-end", flex: 1 }}
            >
              <Dropzone
                onDrop={async (e) => {
                  await handleYamlValidations(e, currentDevice);
                  await handleYamlDrop(e, currentDevice);
                }}
                multiple={false}
                accept=".yaml, .yml"
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()}>
                    <input {...getInputProps({ accept: ".yaml, .yml" })} />

                    <TriggerComponent
                      disabled={disableAllActions || isTriggered}
                      title={
                        isTriggered
                          ? OPP_CONSTANTS.btns.validatingYaml
                          : OPP_CONSTANTS.btns.uploadYaml
                      }
                      icon={"fa-upload"}
                    />
                  </div>
                )}
              </Dropzone>
            </div>
          </div>
          <CertificateComponent
            isAtDevice={true}
            isForSDCard={true}
            deviceDefaultStatus={deviceActionStatus[currentDevice?.id] ?? []}
            deviceStatus={hardwaresQCStatus[currentDevice?.id]}
          />
        </Row>
      </Card>
    );
  };

  return (
    <>
      {statusMessage && (
        <div className="alert-container">
          <ToastComponent
            message={statusMessage}
            duration={5000}
            toastType={"error"}
            handleToastClose={handleToastClose}
          />
        </div>
      )}

      {deviceMapData &&
        Object.keys(deviceMapData).map((deviceModelKey) => {
          const techbookGroup = deviceMapData[deviceModelKey];

          return Object.keys(techbookGroup).map((techbookKey) => {
            const devicesGroup = techbookGroup[techbookKey] || [];
            if (devicesGroup.length === 0) return null;

            const firstDevice = devicesGroup[0];
            const deviceTypeKey = firstDevice.deviceType; // pulled from the first device

            return (
              <Card
                key={`${deviceModelKey}_${techbookKey}`}
                size="small"
                bodyStyle={{
                  backgroundColor:
                    `${HARDWARE_TYPE[deviceTypeKey]?.color ?? HARDWARE_TYPE.TAB.color}33`,
                }}
                headStyle={{
                  backgroundColor:
                    HARDWARE_TYPE[deviceTypeKey]?.color ?? HARDWARE_TYPE.TAB.color,
                  fontSize: "20px",
                  borderTopLeftRadius: "6px",
                  borderTopRightRadius: "4px",
                  color: "#fff",
                }}
                style={{
                  border: `1px solid ${HARDWARE_TYPE[deviceTypeKey]?.color ?? HARDWARE_TYPE.TAB.color}`,
                }}
                title={cardDetailsLayout(
                  deviceModelKey,
                  deviceTypeKey,
                  devicesGroup.length,
                  firstDevice.sdCardProfileName,
                  firstDevice.sdCardSize,
                  firstDevice, // 🔥 NEW
                  stockLocationLoadingMap[
                  `${productType}-${deviceModelKey}-${deviceTypeKey}-${firstDevice?.isTechbook}`
                  ] || false,
                  (val) => setStockLocationLoading(
                    `${productType}-${deviceModelKey}-${deviceTypeKey}-${firstDevice?.isTechbook}`,
                    val
                  )
                )}
                className="device-card-component"
              >
                {devicesGroup.map((keyComponent) => {
                  const currentDevice = devices?.[keyComponent?.id];
                  if (!currentDevice) return null;

                  const isValidNo = !!(currentDevice?.deviceNo?.length) && !(!!serialIdErrors[currentDevice?.id]);

                  switch (deviceTypeKey.toUpperCase()) {
                    case HARDWARE_TYPE.TAB.name.toUpperCase():
                      return tabDetailedLayout(currentDevice, isValidNo);
                    case HARDWARE_TYPE.TV.name.toUpperCase():
                      return tvDetailedLayout(currentDevice, isValidNo);
                    case HARDWARE_TYPE.SDCARD.name.toUpperCase():
                      return sdCardDetailedLayout(currentDevice);
                    case HARDWARE_TYPE.PANEL.name.toUpperCase():
                      return panelDetailedLayout(currentDevice, isValidNo);
                    case HARDWARE_TYPE.PENDRIVE.name.toUpperCase():
                      return penDriveDetailedLayout(currentDevice, isValidNo);
                    default:
                      return null;
                  }
                })}
              </Card>
            );
          });
        })}
    </>
  );
};

export default DeviceColumnComponent;
